import { cn } from "@/lib/utils";
import {
  Icon as TablerIcon,
  IconLoader2,
  IconX,
  IconCircleCheck,
  IconPlaystationX,
  IconAlertCircle,
} from "@tabler/icons-react";
import { IconType } from "react-icons";
import { ReactNode } from "react";

export type ToastCompactProps = {
  icon?: IconType | TablerIcon;
  iconProps?: {
    size?: number;
    strokeWidth?: number;
    className?: string;
  };
  iconBackgroundClassName?: string;
  message?: ReactNode;
  messagePrefix?: string;
  messageHighlight?: string;
  messageSuffix?: string;
  hasDescription?: boolean;
  descriptionPrefix?: string;
  descriptionHighlight?: string;
  descriptionSuffix?: string;
  isError?: boolean;
  isWarning?: boolean;
  isLoading?: boolean;
  onDismiss?: () => void;
};

const ToastCompact = ({
  icon: Icon,
  iconProps = {
    size: 18,
    strokeWidth: 1.5,
  },
  iconBackgroundClassName = "",
  message,
  messagePrefix = "",
  messageHighlight = "",
  messageSuffix = "",
  hasDescription = false,
  descriptionPrefix = "",
  descriptionHighlight = "",
  descriptionSuffix = "",
  isError = false,
  isWarning = false,
  isLoading = false,
  onDismiss,
}: ToastCompactProps) => {
  const iconBackgroundClass = cn(
    "flex w-[30px] h-full justify-center items-center rounded-[8px]",
    isError
      ? "bg-error-default/10"
      : isWarning || isLoading
        ? "bg-[#FF9D0014]"
        : "bg-[#03A28A14]",
    iconBackgroundClassName,
  );

  const messageColorClass = cn(
    "text-[12px] font-normal leading-[16px] text-brand-primary-new",
  );
  const descriptionColorClass = cn("text-brand-primary-600 text-[12px] font-normal");

  return (
    <div
      className={cn(
        "min-w-[300px] w-auto h-[38px] flex items-center gap-3 rounded-[12px] border border-border-gray bg-white p-1 shadow-[0_1px_2px_-1px_rgba(0,0,0,0.10),_0_1px_3px_0_rgba(0,0,0,0.10)]",
        hasDescription && "h-[56px]",
      )}
    >
      <div className={iconBackgroundClass}>
        {Icon ? (
          // Custom icon, if provided
          <Icon
            {...iconProps}
            className={cn(
              isError
                ? "text-error-default"
                : isWarning || isLoading
                  ? "text-brand-orange"
                  : "text-[#03A28A]",
              iconProps.className,
            )}
          />
        ) : isLoading ? (
          <IconLoader2
            {...iconProps}
            className={cn("text-brand-orange animate-spin", iconProps.className)}
          />
        ) : isError ? (
          <IconPlaystationX
            {...iconProps}
            className={cn("text-error-default", iconProps.className)}
          />
        ) : isWarning ? (
          <IconAlertCircle
            {...iconProps}
            className={cn("text-[#FF9D00]", iconProps.className)}
          />
        ) : (
          <IconCircleCheck
            {...iconProps}
            className={cn("text-brand-blue", iconProps.className)}
          />
        )}
      </div>

      <div
        className={cn(
          "flex-1 h-full flex justify-between",
          hasDescription ? "items-start" : "items-center",
        )}
      >
        <div className="w-max flex flex-col h-full justify-center">
          <p className={messageColorClass}>
            {message ?? (
              <>
                {messagePrefix}{" "}
                {messageHighlight && (
                  <span className="font-[500]">{messageHighlight}</span>
                )}{" "}
                {messageSuffix}
              </>
            )}
          </p>
          {hasDescription && (
            <p className={descriptionColorClass}>
              {descriptionPrefix}{" "}
              {descriptionHighlight && (
                <span className="font-[500]">{descriptionHighlight}</span>
              )}{" "}
              {descriptionSuffix}
            </p>
          )}
        </div>
        {onDismiss && (
          <button
            type="button"
            aria-label="Dismiss"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            className="ml-4 rounded-md p-[10px] text-brand-primary-400 hover:text-brand-primary-800"
          >
            <IconX size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

export default ToastCompact;
